import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import dayjs from 'dayjs'

import type { TagDescription } from '@reduxjs/toolkit/query'

/** Calendar months (YYYY-MM) overlapping [fromDate, toDate] inclusive (date part only). */
export function monthsOverlappingApiRange(fromDate: string, toDate: string): string[] {
    const from = dayjs(fromDate.slice(0, 10))
    const to = dayjs(toDate.slice(0, 10))
    if (!from.isValid() || !to.isValid()) return []

    const keys = new Set<string>()
    let m = from.startOf('month')
    const endMonth = to.startOf('month')
    while (m.isBefore(endMonth) || m.isSame(endMonth, 'month')) {
        keys.add(m.format('YYYY-MM'))
        m = m.add(1, 'month')
    }
    return [...keys]
}

/**
 * Month keys to invalidate after availability CRUD so adjacent ISO-week month views refetch.
 * For each calendar month touched by the range, also invalidates M-1 and M+1.
 */
export function monthsToInvalidateForDateRange(fromDate: string, toDate: string): string[] {
    const keys = new Set<string>()
    for (const ym of monthsOverlappingApiRange(fromDate, toDate)) {
        const base = dayjs(`${ym}-01`)
        keys.add(base.format('YYYY-MM'))
        keys.add(base.subtract(1, 'month').format('YYYY-MM'))
        keys.add(base.add(1, 'month').format('YYYY-MM'))
    }
    return [...keys]
}

/** Derive cache invalidation months from details-modal availability / shift payload. */
export function collectInvalidateMonthsFromAvailability(availability: unknown): string[] {
    const keys = new Set<string>()
    const addRange = (from: string, to: string) => {
        for (const k of monthsToInvalidateForDateRange(from, to)) keys.add(k)
    }
    const a = availability as Record<string, unknown> | null | undefined
    if (!a) return []

    try {
        const ranges = a.threadAllRanges as { start?: string; end?: string }[] | undefined
        if (Array.isArray(ranges) && ranges.length > 0) {
            for (const r of ranges) {
                if (typeof r?.start === 'string' && typeof r?.end === 'string') addRange(r.start, r.end)
            }
            if (keys.size) return [...keys]
        }

        const threadRows = a.threadAllPeriodRows as { date?: string }[] | undefined
        if (Array.isArray(threadRows) && threadRows.length > 0) {
            for (const row of threadRows) {
                if (typeof row?.date === 'string') addRange(row.date, row.date)
            }
            if (keys.size) return [...keys]
        }

        const periodRows = a.periodRows as { date?: string }[] | undefined
        if (Array.isArray(periodRows) && periodRows.length > 0) {
            const dates = periodRows.map((x) => x?.date).filter((d): d is string => typeof d === 'string')
            if (dates.length > 0) {
                const sorted = [...dates].sort()
                addRange(sorted[0], sorted[sorted.length - 1])
            }
            if (keys.size) return [...keys]
        }

        if (typeof a.periodStartDate === 'string' && typeof a.periodEndDate === 'string') {
            addRange(a.periodStartDate, a.periodEndDate)
            if (keys.size) return [...keys]
        }

        if (typeof a.date === 'string') {
            addRange(a.date, a.date)
        }
    } catch {
        /* ignore */
    }
    return [...keys]
}

function calendarTagsForRange(fromDate: string, toDate: string): TagDescription<'Calendar'>[] {
    const months = monthsOverlappingApiRange(fromDate, toDate)
    const tags: TagDescription<'Calendar'>[] = [{ type: 'Calendar', id: 'LIST' }]
    for (const id of months) tags.push({ type: 'Calendar', id })
    return tags
}

function calendarTagsToInvalidate(fromDate: string, toDate: string): TagDescription<'Calendar'>[] {
    const months = monthsToInvalidateForDateRange(fromDate, toDate)
    const tags: TagDescription<'Calendar'>[] = []
    for (const id of months) tags.push({ type: 'Calendar', id })
    return tags
}

interface CalendarV2Request {
    fromDate: string
    toDate: string
    scopes: string[]
    work?: string
}

export interface CalendarV2Response {
    availability?: any[]
    shifts?: any[]
    requests?: { items?: any[] }
    vacation?: any[]
    nationalHolidays?: any[]
    birthday?: any
    leaveOfAbsence?: any[]
    absenceData?: {
        workedDays: number
        isLogAllowed: boolean
    }
    [key: string]: any
}

interface CreateAvailabilityRequest {
    availableId: number
    fromDate: string
    toDate: string
    fromTime: string
    toTime: string
    note?: string
    includeWeekends: boolean
}

interface CreateAvailabilityResponse {
    statusCode: number
    success: boolean
    message: string
    data?: {
        count: number
        token: string
    }
}

interface CreateShiftRequest {
    jobId: number
    fromDate: string
    toDate: string
    fromTime: string
    toTime: string
    break?: string
    breakFromTime?: string | null
    breakToTime?: string | null
    note?: string
    includeWeekends: boolean
}

interface WorkByIdResponse {
    statusCode: number
    success: boolean
    message: string
    data?: {
        job?: any
        work?: any
        files?: any[]
        childProject?: any
        workTrip?: any
    }
}

/** Row from `/employee/calendar/availability/token` with JSON body `{ token }` (PHP reads body for any verb). */
export interface CalendarAvailabilityTokenItem {
    calendarId: number
    date: string
    availableId: number
    token?: string
    fromTime?: string
    toTime?: string
    fromDt?: string
    toDt?: string
    note?: string
    jobName?: string
}

export interface CalendarAvailabilityByTokenResponse {
    items: CalendarAvailabilityTokenItem[]
}

export const calendarApi = createApi({
    reducerPath: 'calendarApi',
    tagTypes: ['Calendar'],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)
            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    endpoints: (builder) => ({
        getCalendarV2: builder.query<CalendarV2Response, CalendarV2Request>({
            query: ({ fromDate, toDate, scopes, work }) => {
                console.log('📤 Fetch calendarV2 with:', { fromDate, toDate, scopes })
                const params: Record<string, string> = {
                    fromDate,
                    toDate,
                    scopes: scopes.join(','),
                }
                if (work != null && work !== '') params.work = work
                return {
                    url: '/employee/calendar/v2',
                    method: 'GET',
                    params,
                }
            },
            transformResponse: (response: any): CalendarV2Response => {
                console.log('📥 Raw response from API:', response)

                if (!response || typeof response !== 'object' || !response.data) {
                    console.warn('❗ Unexpected calendar response format:', response)
                    return {}
                }

                console.log('✅ Parsed calendar data:', response.data)
                return response.data as CalendarV2Response
            },
            providesTags: (_result, _error, arg) => calendarTagsForRange(arg.fromDate, arg.toDate),
        }),

        createAvailability: builder.mutation<CreateAvailabilityResponse, CreateAvailabilityRequest>({
            query: (body) => ({
                url: '/employee/calendar/create/availability',
                method: 'POST',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            invalidatesTags: (_result, _error, arg) => calendarTagsToInvalidate(arg.fromDate, arg.toDate),
        }),

        deleteAvailability: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: { deleted: number } },
            { ids: number[]; invalidateCalendarMonths?: string[] }
        >({
            query: ({ ids }) => ({
                url: '/employee/calendar/delete/availability',
                method: 'DELETE',
                body: { ids },
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            invalidatesTags: (_result, _error, arg) =>
                arg.invalidateCalendarMonths?.length
                    ? arg.invalidateCalendarMonths.map((id) => ({ type: 'Calendar' as const, id }))
                    : [{ type: 'Calendar' as const, id: 'LIST' }],
        }),

        deleteAvailabilityByToken: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: { deleted: number } },
            { token: string; invalidateCalendarMonths?: string[] }
        >({
            query: ({ token }) => ({
                url: '/employee/calendar/delete/availability/token',
                method: 'DELETE',
                body: { token },
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            invalidatesTags: (_result, _error, arg) =>
                arg.invalidateCalendarMonths?.length
                    ? arg.invalidateCalendarMonths.map((id) => ({ type: 'Calendar' as const, id }))
                    : [{ type: 'Calendar' as const, id: 'LIST' }],
        }),

        updateAvailability: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: any },
            {
                id: number
                availableId: number
                date: string
                fromTime: string
                toTime: string
                note?: string
                /** Skip cache invalidation (e.g. sequential thread edits — caller refetches once after the loop). */
                skipInvalidate?: boolean
            }
        >({
            query: ({ id, availableId, date, fromTime, toTime, note }) => ({
                url: `/employee/calendar/update/availability/${id}`,
                method: 'PUT',
                body: { availableId, date, fromTime, toTime, note },
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            invalidatesTags: (_result, _error, arg) =>
                arg.skipInvalidate ? [] : calendarTagsToInvalidate(arg.date, arg.date),
        }),

        createShift: builder.mutation<CreateAvailabilityResponse, CreateShiftRequest>({
            query: (body) => ({
                url: '/employee/calendar/create/shift',
                method: 'POST',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        updateShift: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: any },
            {
                id: number
                date: string
                fromTime: string
                toTime: string
                break?: string
                breakFromTime?: string | null
                breakToTime?: string | null
                note?: string
            }
        >({
            query: ({ id, ...body }) => ({
                url: `/employee/calendar/update/shift/${id}`,
                method: 'PUT',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        deleteShift: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: { deleted: number } },
            { id: number; invalidateCalendarMonths?: string[] }
        >({
            query: ({ id }) => ({
                url: `/employee/calendar/delete/shift/${id}`,
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            invalidatesTags: (_result, _error, arg) =>
                arg.invalidateCalendarMonths?.length
                    ? arg.invalidateCalendarMonths.map((id) => ({ type: 'Calendar' as const, id }))
                    : [{ type: 'Calendar' as const, id: 'LIST' }],
        }),
        getWorkById: builder.query<WorkByIdResponse, number>({
            query: (workHourId) => ({
                url: `/employee/work/${workHourId}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),

        /** Full thread rows for a batch token (not limited to the visible month range). */
        getAvailabilityByToken: builder.query<CalendarAvailabilityByTokenResponse, { token: string }>({
            query: ({ token }) => ({
                url: '/employee/calendar/availability/token',
                method: 'POST',
                body: { token },
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
            transformResponse: (response: unknown): CalendarAvailabilityByTokenResponse => {
                if (!response || typeof response !== 'object' || !('data' in response)) {
                    return { items: [] }
                }
                const data = (response as { data?: { items?: CalendarAvailabilityTokenItem[] } }).data
                const items = data?.items
                return { items: Array.isArray(items) ? items : [] }
            },
        }),
    }),
})

export const {
    useGetCalendarV2Query,
    useCreateAvailabilityMutation,
    useDeleteAvailabilityMutation,
    useDeleteAvailabilityByTokenMutation,
    useUpdateAvailabilityMutation,
    useUpdateShiftMutation,
    useCreateShiftMutation,
    useDeleteShiftMutation,
    useGetWorkByIdQuery,
    useLazyGetAvailabilityByTokenQuery,
} = calendarApi
